/**
 * The weather locations a weather row can pick from — the `locations.json`
 * static asset the lona server serves — as one frozen table per client
 * (`client.getWeatherLocations()`).
 */
import type { Option } from "@tento-lona/sheets";
import { Json, type JsonObject } from "@tento-lona/sheets";
import { ResourceClient } from "@tento-core/resource-client";

/** A weather location: its id, labels and timezone. */
export type WeatherLocation = {
  readonly id: number;
  readonly label: string;
  readonly urlLabel: string;
  readonly tzid: string;
  readonly region: string;
};

/**
 * The weather-location table: every location in the asset's order, and the same locations by id. Frozen — the table, its
 * list and its entries; `byId` is read-only.
 */
export type WeatherLocations = {
  readonly all: readonly WeatherLocation[];
  readonly byId: ReadonlyMap<number, WeatherLocation>;
};

/** A `ReadonlyMap` over a map no caller holds: no `set`, `delete` or `clear`. */
class ReadonlyLocationMap implements ReadonlyMap<number, WeatherLocation> {
  readonly #map: Map<number, WeatherLocation>;
  constructor(map: Map<number, WeatherLocation>) {
    this.#map = map;
    Object.freeze(this);
  }
  get size(): number {
    return this.#map.size;
  }
  get(id: number): WeatherLocation | undefined {
    return this.#map.get(id);
  }
  has(id: number): boolean {
    return this.#map.has(id);
  }
  forEach(
    fn: (value: WeatherLocation, key: number, map: ReadonlyMap<number, WeatherLocation>) => void,
  ): void {
    this.#map.forEach((v, k) => fn(v, k, this));
  }
  keys(): MapIterator<number> {
    return this.#map.keys();
  }
  values(): MapIterator<WeatherLocation> {
    return this.#map.values();
  }
  entries(): MapIterator<[number, WeatherLocation]> {
    return this.#map.entries();
  }
  [Symbol.iterator](): MapIterator<[number, WeatherLocation]> {
    return this.#map.entries();
  }
}

/** Values that go with `WeatherLocations`: `PATH`, `of`, `parse`, `FALLBACK`. */
export namespace WeatherLocations {
  /** The asset path, resolved against the client's `baseUrl`. */
  export const PATH = "/@/static/weather/locations.json";

  /**
   * A frozen table of `all`. Throws `TypeError` when an id repeats. The
   * table names no default: a row with no location shows none.
   */
  export function of(all: readonly WeatherLocation[]): WeatherLocations {
    const entries = all.map((l) => Object.freeze({ ...l }));
    const byId = new Map<number, WeatherLocation>();
    for (const l of entries) {
      if (byId.has(l.id)) throw new TypeError(`WeatherLocations: location id ${l.id} repeats`);
      byId.set(l.id, l);
    }
    return Object.freeze({
      all: Object.freeze(entries),
      byId: new ReadonlyLocationMap(byId),
    });
  }

  /**
   * The `locations.json` asset (`{ locations: [{ id, label, urlLabel, tzid,
   * region }] }`) as a table; a served `defaultLocationId` is ignored. Throws
   * `TypeError` on a malformed asset.
   */
  export function parse(raw: Option<Json>): WeatherLocations {
    const obj = Json.object(raw);
    const list = Json.array(obj, "locations");
    if (list == null) {
      throw new TypeError("WeatherLocations: the asset has no `locations`");
    }
    return of(list.map((item, i) => locationOf(Json.object(item), i)));
  }

  /**
   * The viewer's weather location, or `null`, for the viewer's timezone
   * `tzid` (their effective timezone,
   * `client.preferences.timezone().effectiveTimezoneName()`): the one
   * location of `table` whose `tzid` it is; or, when the zone has several,
   * the one that is the zone's named city — the `label` that equals the
   * zone name's last segment with underscores read as spaces
   * (`America/Los_Angeles` → Los Angeles), or that segment followed by
   * " City" (`Asia/Ho_Chi_Minh` → Ho Chi Minh City), compared ignoring case
   * and diacritics, nothing looser. An exact label beats a "+ City" one in
   * the same zone; two labels that match only with " City" answer `null`.
   * A zone with no location, several of which
   * none is its named city, or a name that is no city (`US/Pacific`,
   * `Etc/UTC`) answers `null`; so does a `null` timezone. Never a location the viewer's place does not name. A
   * new weather row takes it as its location; with `null` it is created
   * with none (the location picker chooses one). A stored row is never
   * given one.
   */
  export function forViewer(table: WeatherLocations, tzid: Option<string>): Option<WeatherLocation> {
    if (tzid == null) return null;
    const inZone = table.all.filter((location) => location.tzid === tzid);
    if (inZone.length <= 1) return inZone[0] ?? null;
    const city = cityKey(tzid.slice(tzid.lastIndexOf("/") + 1));
    const exact = inZone.filter((location) => cityKey(location.label) === city);
    if (exact.length > 0) return exact.length === 1 ? exact[0] ?? null : null;
    const suffixed = inZone.filter((location) => cityKey(location.label) === `${city} city`);
    return suffixed.length === 1 ? suffixed[0] ?? null : null;
  }

  /**
   * The table a surface shows before its client's own has loaded: the four
   * locations the asset has always opened with. A display table only, never
   * a default: no viewer's weather falls back to one of these. Every entry
   * is also in the asset.
   */
  export const FALLBACK: Readonly<WeatherLocations> = of([
    {
      id: 2550230,
      label: "San Francisco",
      urlLabel: "san-francisco-california-united-states-of-america",
      tzid: "America/Los_Angeles",
      region: "North America",
    },
    {
      id: 2550002,
      label: "Redwood City",
      urlLabel: "redwood-city-california-united-states-of-america",
      tzid: "America/Los_Angeles",
      region: "North America",
    },
    {
      id: 2618724,
      label: "New York",
      urlLabel: "new-york-new-york-united-states-of-america",
      tzid: "America/New_York",
      region: "North America",
    },
    {
      id: 3125553,
      label: "Tokyo",
      urlLabel: "tokyo-tokyo-japan",
      tzid: "Asia/Tokyo",
      region: "Asia",
    },
  ]);
}

/** `name` for comparing city names: underscores as spaces, no diacritics, case folded. */
function cityKey(name: string): string {
  return name.replaceAll("_", " ").normalize("NFD").replace(/\p{M}/gu, "").toLowerCase();
}

function locationOf(obj: Option<JsonObject>, index: number): WeatherLocation {
  const id = Json.number(obj, "id");
  const label = Json.string(obj, "label");
  const urlLabel = Json.string(obj, "urlLabel");
  const tzid = Json.string(obj, "tzid");
  const region = Json.string(obj, "region");
  if (id == null || label == null || urlLabel == null || tzid == null || region == null) {
    throw new TypeError(`WeatherLocations: location ${index} lacks an id, label, urlLabel, tzid or region`);
  }
  return { id, label, urlLabel, tzid, region };
}

/**
 * One client's weather-location table: loaded on first ask through the
 * client's `baseUrl` and `fetch`, memoized, and asked again after a failed
 * load (a rejected load is never cached). Package-internal.
 */
export class WeatherLocationsLoader {
  readonly #resources: ResourceClient;
  #table: Option<WeatherLocations> = null;
  #inFlight: Option<Promise<WeatherLocations>> = null;

  /** `baseUrl`: the client's API origin (`null` for same-origin); `fetchFn`:
   *  the client's transport (`null` for the global `fetch`, looked up at send time). */
  constructor(baseUrl: Option<string>, fetchFn: Option<typeof fetch>) {
    const origin = baseUrl ?? "";
    this.#resources = ResourceClient.currentOrigin().withFetcher((url) => {
      const target = url.startsWith("/") ? origin + url : url;
      return fetchFn != null ? fetchFn(target) : globalThis.fetch(target);
    });
  }

  /** The loaded table, or `null` before the first load settles (a synchronous read). */
  peek(): Option<WeatherLocations> {
    return this.#table;
  }

  /** The table: the memoized one, the load in flight, or a new load. */
  load(): Promise<WeatherLocations> {
    if (this.#table != null) return Promise.resolve(this.#table);
    if (this.#inFlight != null) return this.#inFlight;
    const pending = this.#resources.json(WeatherLocations.PATH, WeatherLocations.parse).then(
      (table) => {
        this.#table = table;
        this.#inFlight = null;
        return table;
      },
      (err: unknown) => {
        this.#inFlight = null;
        throw err;
      },
    );
    this.#inFlight = pending;
    return pending;
  }
}
